import { motion } from 'framer-motion';
import { Star } from 'lucide-react';
import { Card } from './ui/card';

// Google "G" als lokales SVG - keine externe Verbindung
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const googleReviews = [
  {
    name: 'A. K.',
    rating: 5,
    text: 'Der Verkauf meines PKWs verlief völlig reibungslos. Super netter Kontakt und seriöse Vorgehensweise. Es gibt nichts zu bemängeln und kann den Händler daher absolut weiterempfehlen.',
    date: 'vor 2 Monaten',
  },
  {
    name: 'Flawleess',
    rating: 5,
    text: 'Absolut zufrieden! Der gesamte Ablauf – von der ersten Besichtigung bis zur Übergabe – war professionell, freundlich und transparent. Auch der Preis war fair und angemessen. Der Service war top.',
    date: 'vor 1 Jahr',
  },
  {
    name: 'Florian',
    rating: 5,
    text: 'Super freundlicher Verkäufer, der etwas von seinem Fach versteht. Innerhalb von 5 Tagen wurde das KFZ frisch getüvt und der Service gemacht. Auto in einem guten Zustand. Gerne wieder 👍',
    date: 'vor 10 Monaten',
  },
  {
    name: 'Simon Abele',
    rating: 5,
    text: 'Sehr freundlicher und zuvorkommender Kundenumgang, schnelle Antwortzeit und sehr lösungsorientierte und seriöse Bearbeitung von Anfragen und Kunden-Wünschen. Vielen Dank!',
    date: 'vor 1 Jahr',
  },
  {
    name: 'Tim F.',
    rating: 5,
    text: 'Ein sehr freundlicher und umgänglicher Autohändler. Wir wurden nett empfangen und der Kauf konnte reibungslos und schnell durchgeführt werden. Gerne wieder 👍',
    date: 'Local Guide',
  },
  {
    name: 'saysame',
    rating: 5,
    text: 'Sehr netter Händler. Ziemlich bedacht, dass seine potenziellen Käufer zufrieden sind. Hat alles unkompliziert erledigt und sogar noch extra Service geboten. Ich empfehle ihn jedem weiter.',
    date: 'Langjähriger Kunde',
  },
];

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <Star
        key={star}
        className={`w-4 h-4 ${
          star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
        }`}
      />
    ))}
  </div>
);

export const Reviews = () => {
  // Stand der Gesamtbewertungen (rechtliche Absicherung gegen zukünftige Änderungen)
  const REVIEW_SNAPSHOT_DATE = '20.04.2026';
  const TOTAL_REVIEW_COUNT = 16;
  const AVERAGE_RATING = '5.0';

  return (
    <section id="bewertungen" data-testid="reviews-section" className="py-20 md:py-28 bg-tiktok-surface">
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-sm font-semibold uppercase tracking-[0.2em] text-tiktok-pink mb-4 block">
            Kundenstimmen
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
            Das sagen unsere Kunden
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Überzeugen Sie sich selbst von unserem Service – echte Bewertungen von zufriedenen Kunden.
          </p>
        </motion.div>

        {/* Rating Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex justify-center mb-12"
        >
          {/* Google Rating - mit lokalem Icon */}
          <div className="flex items-center gap-4 px-6 py-4 bg-tiktok-black rounded-2xl border border-white/10">
            <GoogleIcon className="w-8 h-8" />
            <div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold text-white">{AVERAGE_RATING}</span>
                <StarRating rating={5} />
              </div>
              <p className="text-sm text-gray-400">{TOTAL_REVIEW_COUNT} Bewertungen</p>
              <p className="text-xs text-gray-500 mt-1">Stand: {REVIEW_SNAPSHOT_DATE}</p>
            </div>
          </div>
        </motion.div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {googleReviews.map((review, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
            >
              <Card
                data-testid={`review-card-${index}`}
                className="review-card p-6 rounded-2xl border border-white/10 h-full bg-gradient-to-br from-tiktok-black to-tiktok-surface"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-tiktok-cyan to-tiktok-pink flex items-center justify-center text-white font-bold">
                      {review.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-white">{review.name}</p>
                      <p className="text-xs text-gray-500">{review.date}</p>
                    </div>
                  </div>
                  <GoogleIcon className="w-5 h-5 opacity-50" />
                </div>
                <StarRating rating={review.rating} />
                <p className="mt-3 text-gray-300 text-sm leading-relaxed">
                  "{review.text}"
                </p>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Info statt externer Link */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <p className="text-gray-500 text-sm">
            Bewertungen basieren auf echten Kundenerfahrungen.<br />
            <span className="text-gray-600">Auszug aus {TOTAL_REVIEW_COUNT} öffentlichen Google-Bewertungen · Stand: {REVIEW_SNAPSHOT_DATE}</span>
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Reviews;
